<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import ProgressSpinner from 'primevue/progressspinner'
import FolderSharedIcon from '@/assets/folder_shared.svg'
import { useLibraryStore } from '@/library/library.store'
import { useUserStore } from '@/authentication/user.store'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { timeAgo } from '@/util/time'
import { toolkitUrls } from '@/util/links'
import type { LibraryFolder } from '@/library/library_types'

// The API returns one folder more than Home lists.
const MAX_FOLDERS_SHOWN = 3
const MAX_FOLDER_NAME_LENGTH = 34
const ALL_FOLDERS_URL = `${toolkitUrls.home}/v/library/all`

const libraryStore = useLibraryStore()
const { recentFolders } = storeToRefs(libraryStore)
const { userId } = storeToRefs(useUserStore())
const { betaVisibility } = storeToRefs(useVisibilityStore())

const isLoading = ref(false)
const hasLoadError = ref(false)
const hasLoadedFolders = ref(false)

const shownFolders = computed(() => recentFolders.value.slice(0, MAX_FOLDERS_SHOWN))
const hasFolders = computed(() => shownFolders.value.length > 0)
const isEmptyStateShown = computed(
  () => hasLoadedFolders.value && !isLoading.value && !hasLoadError.value && !hasFolders.value
)

function isSharedFolder(folder: LibraryFolder): boolean {
  return (folder.contributors?.length ?? 0) > 0
}

function truncatedName(name: string): string {
  if (name.length < MAX_FOLDER_NAME_LENGTH) {
    return name
  }
  return `${name.substring(0, MAX_FOLDER_NAME_LENGTH)}...`
}

function folderUrl(folder: LibraryFolder, index: number): string {
  const params = new URLSearchParams({
    ref: 'home',
    updatedAt: folder.updated_at ?? '',
    position: String(index + 1)
  })
  if (betaVisibility.value) {
    params.append('beta', 'true')
  }
  return `${toolkitUrls.home}/v/folder/${folder.uuid}?${params.toString()}`
}

async function loadRecentFolders() {
  isLoading.value = true
  hasLoadError.value = false
  try {
    await libraryStore.fetchRecentFolders(userId.value)
    hasLoadedFolders.value = true
  } catch (error) {
    console.error(`Error fetching recent folders: ${error}`)
    hasLoadError.value = true
  } finally {
    isLoading.value = false
  }
}

watch(
  userId,
  () => {
    if (!userId.value) {
      return
    }
    loadRecentFolders()
  },
  { immediate: true }
)
</script>

<template>
  <div aria-labelledby="recent-library-folders" class="mb-8">
    <h3 id="recent-library-folders" class="mb-4 mt-5 text-display-sm font-medium leading-[1.1] text-gray-800">
      Library
    </h3>
    <div v-if="isLoading" class="inline-flex items-center text-gray-700">
      <ProgressSpinner strokeWidth="8" class="h-5 w-5 stroke-primary-400" />
      <span class="ml-2">Loading recent library folders...</span>
    </div>
    <div v-if="hasLoadError" role="alert" class="mb-5 rounded-xs border border-red-100 bg-red-100 p-4 text-red-300">
      Error loading library folders. Please refresh the page and try again.
    </div>
    <ul class="m-0 w-[65%] list-none pl-1">
      <li
        v-for="(folder, index) in shownFolders"
        :key="folder.uuid"
        class="mb-2 flex items-center justify-between"
        data-testid="recent-folder"
      >
        <a
          class="fs-exclude group flex items-center gap-2.5 no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-blue-400 max-[992px]:w-full"
          :href="folderUrl(folder, index)"
          :aria-label="`Library folder: ${folder.name}`"
        >
          <img v-if="isSharedFolder(folder)" :src="FolderSharedIcon" alt="" aria-hidden="true" class="h-8 w-8" />
          <span v-else class="material-icons-round text-2xl text-gray-700" aria-hidden="true">folder</span>
          <span class="text-blue-400 group-hover:text-blue-500 group-hover:underline" data-testid="folder-name">{{ truncatedName(folder.name) }}</span>
        </a>
        <span class="self-center text-right text-[0.8rem] italic max-[992px]:hidden">
          Updated {{ timeAgo(folder.updated_at) }}
        </span>
      </li>
    </ul>
    <a v-if="hasFolders" :href="ALL_FOLDERS_URL" class="text-blue-400 hover:text-blue-500 hover:underline">View all folders</a>
    <template v-if="isEmptyStateShown">
      <p class="mt-8 italic">You don't have any folders in your library.</p>
      <a
        :href="ALL_FOLDERS_URL"
        class="rounded-sm border-2 border-blue-400 bg-white px-3 py-1 text-blue-400 hover:underline"
        data-testid="create-folder-link"
      >
        Create new folder
      </a>
    </template>
  </div>
</template>
